/* @ngInject */

export default (
  $q,
  $http,
  $rootScope,
  CacheFactory,
  logger,
  dataService,
  Product,
  PRODUCT
) => {
  let cache = null;
  if (!CacheFactory.get(PRODUCT.CACHE.STORE.NAME)) {
    cache = CacheFactory(PRODUCT.CACHE.STORE.NAME, {
      maxAge: PRODUCT.CACHE.STORE.TTL,
      deleteOnExpire: PRODUCT.CACHE.STORE.DELETION,
      recycleFreq: PRODUCT.CACHE.STORE.RECYCLE,
      storageMode: PRODUCT.CACHE.STORE.STORAGE
    });
  }

  /**
   * Build Product(s) model(s).
   *
   * @param {Object|Object[]} [products] - Product(s) data.
   *
   * @returns {Product|Product[]}
   */
  const build = (products) => {
    if (!_.isArray(products)) {
      return Product.Build(products);
    }
    return products.map((product) => Product.Build(product));
  };

  /**
   * Clear all data from cache.
   */
  const clearCache = () => {
    logger.debug('productsDataFactory', 'clearCache', 'Clearing all data in the cache');
    cache.removeAll();
  };

  /**
   * Get the asset finance available product(s).
   *
   * @param {Object} [params]
   * @param {Object} [params.idLenders] - Include only Lender(s) with given ID(s).
   *
   * @returns {Array}
   */
  const getAssetFinanceAvailable = (params) => {
    if (!params) {
      logger.error(
        'productsDataFactory',
        'getAssetFinanceAvailable',
        'Not all required parameters are present'
      );
      return null;
    }
    const ignoreCache = params?.ignoreCache === true;
    const cacheKey = JSON.stringify({
      idAccount: $rootScope.CurrentAccount.id,
      idOrganization: $rootScope.CurrentOrganization.id,
      type: 'productsAssetFinanceAvailable',
      params
    });

    logger.log(
      'productsDataFactory',
      'getAssetFinanceAvailable',
      'Retrieving data via dataService'
    );
    return dataService.apiRequest(
      cache,
      cacheKey,
      ignoreCache,
      build,
      $http.post,
      PRODUCT.API.ASSET_FINANCE_AVAILABLE,
      { params }
    );
  };

  /**
   * Get the asset finance net monthly surplus.
   *
   * @param {Object} [params]
   * @param {Object} [params.idProducts] - Include only Product(s) with given ID(s).
   *
   * @returns {Array}
   */
  const getAssetFinanceNetMonthlySurplus = (params) => {
    if (!params) {
      logger.error(
        'productsDataFactory',
        'getAssetFinanceNetMonthlySurplus',
        'Not all required parameters are present'
      );
      return null;
    }
    const ignoreCache = params?.ignoreCache === true;
    const cacheKey = JSON.stringify({
      idAccount: $rootScope.CurrentAccount.id,
      idOrganization: $rootScope.CurrentOrganization.id,
      type: 'productsAssetFinanceNetMonthlySurplus',
      params
    });

    logger.log(
      'productsDataFactory',
      'getAssetFinanceNetMonthlySurplus',
      'Retrieving data via dataService'
    );
    return dataService.apiRequest(
      cache,
      cacheKey,
      ignoreCache,
      build,
      $http.post,
      PRODUCT.API.ASSET_FINANCE_NET_MONTHLY_SURPLUS,
      { params }
    );
  };

  /**
   * Get the available home loan product(s).
   *
   * @param {Object} [params]
   * @param {Object} [params.idLenders] - Include only Lender(s) with given ID(s).
   *
   * @returns {Array}
   */
  const getHomeLoanAvailable = (params) => {
    if (!params) {
      logger.error(
        'productsDataFactory',
        'getHomeLoanAvailable',
        'Not all required parameters are present'
      );
      return null;
    }
    const ignoreCache = params?.ignoreCache === true;
    const cacheKey = JSON.stringify({
      idAccount: $rootScope.CurrentAccount.id,
      idOrganization: $rootScope.CurrentOrganization.id,
      type: 'productsHomeLoanAvailable',
      params
    });

    logger.log(
      'productsDataFactory',
      'getHomeLoanAvailable',
      'Retrieving data via dataService'
    );
    return dataService.apiRequest(
      cache,
      cacheKey,
      ignoreCache,
      build,
      $http.post,
      PRODUCT.API.HOME_LOAN_AVAILABLE,
      { params }
    );
  };

  /**
   * Get home loan comparison rate.
   *
   * @param {Object} [params]
   * @param {Object} [params.idProducts] - Include only Product(s) with given ID(s).
   * @param {Object} [params.estimateType] - Include only estimate result with given type.
   *
   * @returns {Array}
   */
  const getHomeLoanComparisonRate = (params) => {
    if (!params || !params.idProducts) {
      logger.error(
        'productsDataFactory',
        'getHomeLoanComparisonRate',
        'Not all required parameters are present'
      );
      return null;
    }
    const ignoreCache = params?.ignoreCache === true;
    const cacheKey = JSON.stringify({
      idAccount: $rootScope.CurrentAccount.id,
      idOrganization: $rootScope.CurrentOrganization.id,
      type: 'productsHomeLoanComparisonRate',
      params
    });

    logger.log(
      'productsDataFactory',
      'getHomeLoanComparisonRate',
      'Retrieving data via dataService'
    );
    return dataService.apiRequest(
      cache,
      cacheKey,
      ignoreCache,
      build,
      $http.post,
      PRODUCT.API.HOME_LOAN_COMPARISON_RATE,
      { params }
    );
  };

  /**
   * Get home loan maximum borrowing.
   *
   * @param {Object} [params]
   * @param {Object} [params.idProducts] - Include only Product(s) with given ID(s).
   * @param {Object} [params.estimateType] - Include only estimate result with given type.
   *
   * @returns {Array}
   */
  const getHomeLoanMaximumBorrowing = (params) => {
    if (!params || !params.idProducts) {
      logger.error(
        'productsDataFactory',
        'getHomeLoanMaximumBorrowing',
        'Not all required parameters are present'
      );
      return null;
    }
    const ignoreCache = params?.ignoreCache === true;
    const cacheKey = JSON.stringify({
      idAccount: $rootScope.CurrentAccount.id,
      idOrganization: $rootScope.CurrentOrganization.id,
      type: 'productsHomeLoanMaximumBorrowing',
      params
    });

    logger.log(
      'productsDataFactory',
      'getHomeLoanMaximumBorrowing',
      'Retrieving data via dataService'
    );
    return dataService.apiRequest(
      cache,
      cacheKey,
      ignoreCache,
      build,
      $http.post,
      PRODUCT.API.HOME_LOAN_MAXIMUM_BORROWING,
      { params }
    );
  };

  return {
    build,
    clearCache,
    getAssetFinanceAvailable,
    getAssetFinanceNetMonthlySurplus,
    getHomeLoanAvailable,
    getHomeLoanComparisonRate,
    getHomeLoanMaximumBorrowing
  };
};
